import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { priceItems } from "@/mocks/prices";
import { formatPercent, formatPrice, directionClass } from "@/utils/format";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "黄金行情", to: "/category/gold-price" },
  { label: "投资资讯", to: "/category/investment-news" },
  { label: "关于我们", to: "/about" },
];

function MarketTicker() {
  const items = priceItems.slice(0, 6);
  const loop = [...items, ...items];

  return (
    <div className="bg-accent-950 border-b border-white/5">
      <div className="flex items-center px-4 md:px-6 h-9 overflow-hidden">
        <span className="flex items-center gap-1.5 text-primary-400 text-xs font-label whitespace-nowrap mr-4 shrink-0">
          <i className="ri-pulse-line text-sm" />
          行情快讯
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex items-center gap-8 animate-marquee whitespace-nowrap">
            {loop.map((item, index) => (
              <span
                key={`${item.id}-${index}`}
                className="flex items-center gap-2 text-xs text-foreground-200"
              >
                <span className="text-foreground-100">{item.name}</span>
                <span className="font-medium">{formatPrice(item.price)}</span>
                <span className={`flex items-center gap-0.5 ${directionClass(item.changePercent)}`}>
                  <i className={item.changePercent >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                  {formatPercent(item.changePercent)}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setKeyword("");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50">
      <MarketTicker />
      <div className="bg-accent-950/98 backdrop-blur border-b border-white/5">
        <div className="flex items-center justify-between gap-4 px-4 md:px-6 h-16">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-accent-950">
              <i className="ri-coin-line text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg font-heading font-bold text-primary-400 tracking-wide">黄金</span>
              <span className="text-[10px] tracking-[0.25em] text-foreground-300 mt-0.5">GOLD PORTAL</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-md text-sm font-label transition-colors ${
                  isActive(link.to)
                    ? "text-primary-400 bg-white/5"
                    : "text-foreground-100 hover:text-primary-400 hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex items-center relative">
            <i className="ri-search-line absolute left-3 text-foreground-300 text-base pointer-events-none" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索行情 / 资讯"
              className="w-52 lg:w-64 h-10 pl-9 pr-3 rounded-md bg-white/5 border border-white/10 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-white/10 transition-colors"
            />
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-50 hover:bg-white/10 transition-colors"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-white/5 bg-accent-950 px-4 pb-5 pt-4 animate-fade-in">
            <form onSubmit={handleSearch} className="flex items-center relative mb-4">
              <i className="ri-search-line absolute left-3 text-foreground-300 text-base pointer-events-none" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索行情 / 资讯"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-white/5 border border-white/10 text-sm text-foreground-50 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </form>
            <nav className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={`px-3 py-3 rounded-md text-sm font-label border-b border-white/5 last:border-0 ${
                    isActive(link.to) ? "text-primary-400" : "text-foreground-100"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}